import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DEFAULT_CHAT_MODEL_ID,
  normalizeChatModelId,
} from '../chatbot-ia/chatbot-models';

export type ChatFontSize = 'sm' | 'md' | 'lg';

export type ChatResponseStyle =
  | 'compact'
  | 'friendly'
  | 'detailed'
  | 'didactic'
  | 'executive';

export interface ChatbotSettings {
  version: 1 | 2;
  fontSize: ChatFontSize;
  responseStyle: ChatResponseStyle;
  openWithSidebar: boolean;
  openNewConversationOnLaunch: boolean;
  keepSidebarOnSelect: boolean;
  enterToSend: boolean;
  showKeyboardHints: boolean;
  compactDensity: boolean;
  showTimestamps: boolean;
  expandProcessByDefault: boolean;
  ttsEnabled: boolean;
  voiceLoopEnabled: boolean;
  /** Admin-only no composer; default Gemini 3.8 Flash. */
  preferredChatModel: string;
}

export const CHAT_RESPONSE_STYLE_OPTIONS: {
  id: ChatResponseStyle;
  label: string;
  description: string;
  icon: string;
  isDefault?: boolean;
}[] = [
  {
    id: 'compact',
    label: 'Compacto',
    description: 'Direto, 1–3 frases. O padrão atual.',
    icon: 'short_text',
    isDefault: true,
  },
  {
    id: 'friendly',
    label: 'Simpático',
    description: 'Caloroso, com jeito de conversa.',
    icon: 'mood',
  },
  {
    id: 'detailed',
    label: 'Detalhado',
    description: 'Explica contexto, implicações e o próximo passo.',
    icon: 'notes',
  },
  {
    id: 'didactic',
    label: 'Didático',
    description: 'Passo a passo, ensina o porquê.',
    icon: 'school',
  },
  {
    id: 'executive',
    label: 'Executivo',
    description: 'Conclusão primeiro, depois os fatos.',
    icon: 'summarize',
  },
];

export const CHAT_FONT_SIZE_SCALE: Record<ChatFontSize, number> = {
  sm: 0.875,
  md: 1,
  lg: 1.2,
};

export const DEFAULT_CHATBOT_SETTINGS: ChatbotSettings = {
  version: 2,
  fontSize: 'md',
  responseStyle: 'compact',
  openWithSidebar: false,
  openNewConversationOnLaunch: true,
  keepSidebarOnSelect: false,
  enterToSend: true,
  showKeyboardHints: true,
  compactDensity: false,
  showTimestamps: true,
  expandProcessByDefault: false,
  ttsEnabled: false,
  voiceLoopEnabled: false,
  preferredChatModel: DEFAULT_CHAT_MODEL_ID,
};

const STORE_VERSION = 2 as const;
const STORAGE_KEY = 'wosi-chat-settings:v1';
const FONT_SIZES = new Set<ChatFontSize>(['sm', 'md', 'lg']);
const RESPONSE_STYLES = new Set<ChatResponseStyle>(
  CHAT_RESPONSE_STYLE_OPTIONS.map((option) => option.id)
);

@Injectable({ providedIn: 'root' })
export class ChatbotSettingsService {
  private platformId = inject(PLATFORM_ID);
  private memoryCache: ChatbotSettings | null = null;

  load(): ChatbotSettings {
    if (this.memoryCache) {
      return { ...this.memoryCache };
    }

    this.memoryCache = this.read();
    return { ...this.memoryCache };
  }

  save(patch: Partial<ChatbotSettings>): ChatbotSettings {
    const next = this.normalize({
      ...this.load(),
      ...patch,
      version: STORE_VERSION,
    });
    this.memoryCache = next;
    this.write(next);
    return { ...next };
  }

  reset(): ChatbotSettings {
    this.memoryCache = { ...DEFAULT_CHATBOT_SETTINGS };
    this.write(this.memoryCache);
    return { ...this.memoryCache };
  }

  private read(): ChatbotSettings {
    if (!isPlatformBrowser(this.platformId)) {
      return { ...DEFAULT_CHATBOT_SETTINGS };
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_CHATBOT_SETTINGS };

      const parsed = JSON.parse(raw) as Partial<ChatbotSettings>;
      const next = this.normalize(parsed);
      if (parsed?.version !== STORE_VERSION) {
        this.write(next);
      }
      return next;
    } catch {
      return { ...DEFAULT_CHATBOT_SETTINGS };
    }
  }

  private write(settings: ChatbotSettings): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // quota exceeded — fail silently
    }
  }

  private normalize(raw: Partial<ChatbotSettings> | null | undefined): ChatbotSettings {
    const source = raw ?? {};
    const isLegacy = source.version !== STORE_VERSION;

    return {
      version: STORE_VERSION,
      fontSize: FONT_SIZES.has(source.fontSize as ChatFontSize)
        ? (source.fontSize as ChatFontSize)
        : DEFAULT_CHATBOT_SETTINGS.fontSize,
      responseStyle: RESPONSE_STYLES.has(source.responseStyle as ChatResponseStyle)
        ? (source.responseStyle as ChatResponseStyle)
        : DEFAULT_CHATBOT_SETTINGS.responseStyle,
      openWithSidebar: this.asBoolean(
        source.openWithSidebar,
        DEFAULT_CHATBOT_SETTINGS.openWithSidebar
      ),
      openNewConversationOnLaunch: this.asBoolean(
        source.openNewConversationOnLaunch,
        DEFAULT_CHATBOT_SETTINGS.openNewConversationOnLaunch
      ),
      keepSidebarOnSelect: this.asBoolean(
        source.keepSidebarOnSelect,
        DEFAULT_CHATBOT_SETTINGS.keepSidebarOnSelect
      ),
      enterToSend: this.asBoolean(
        source.enterToSend,
        DEFAULT_CHATBOT_SETTINGS.enterToSend
      ),
      showKeyboardHints: this.asBoolean(
        source.showKeyboardHints,
        DEFAULT_CHATBOT_SETTINGS.showKeyboardHints
      ),
      compactDensity: this.asBoolean(
        source.compactDensity,
        DEFAULT_CHATBOT_SETTINGS.compactDensity
      ),
      showTimestamps: isLegacy
        ? true
        : this.asBoolean(
            source.showTimestamps,
            DEFAULT_CHATBOT_SETTINGS.showTimestamps
          ),
      expandProcessByDefault: this.asBoolean(
        source.expandProcessByDefault,
        DEFAULT_CHATBOT_SETTINGS.expandProcessByDefault
      ),
      ttsEnabled: this.asBoolean(
        source.ttsEnabled,
        DEFAULT_CHATBOT_SETTINGS.ttsEnabled
      ),
      voiceLoopEnabled: this.asBoolean(
        source.voiceLoopEnabled,
        DEFAULT_CHATBOT_SETTINGS.voiceLoopEnabled
      ),
      preferredChatModel: normalizeChatModelId(source.preferredChatModel),
    };
  }

  private asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }
}
